import { Fraction } from '@learnforge/demo-math/numeric';

// ── Types ─────────────────────────────────────────────────────────────────────

/** The named number sets in the standard nesting ℕ ⊂ ℤ ⊂ ℚ ⊂ ℝ ⊂ ℂ. */
export type NumberSet = 'N' | 'Z' | 'Q' | 'R' | 'C';

/**
 * A parsed numeric value tagged by its structural kind.
 *
 * - `integer`: a whole number (positive, negative, or zero).
 * - `rational`: a NON-integer rational, stored as a fully-reduced Fraction
 *   (den > 1 after reduction).
 * - `irrational`: a transcendental or algebraic irrational; stored as an
 *   approximate float and a display label (e.g. `'√2'`, `'π'`).
 * - `complex`: a number with non-zero imaginary part (im ≠ 0).
 */
export type ParsedNumber =
  | { kind: 'integer'; value: number }
  | { kind: 'rational'; fraction: Fraction } // non-integer rational
  | { kind: 'irrational'; approx: number; label: string } // √2, √3, π, e, …
  | { kind: 'complex'; re: number; im: number }; // im ≠ 0

// ── Constants ─────────────────────────────────────────────────────────────────

/**
 * Small lookup table for well-known irrational constants.
 * Keys are the canonical token(s) accepted by parseNumber.
 */
const IRRATIONAL_TABLE: ReadonlyArray<{
  tokens: readonly string[];
  label: string;
  approx: number;
}> = [
  { tokens: ['√2', 'sqrt2'], label: '√2', approx: Math.SQRT2 },
  { tokens: ['√3', 'sqrt3'], label: '√3', approx: Math.sqrt(3) },
  { tokens: ['π', 'pi'], label: 'π', approx: Math.PI },
  { tokens: ['e'], label: 'e', approx: Math.E },
];

// ── Membership predicates ─────────────────────────────────────────────────────

/**
 * Returns true iff `v` is a natural number.
 *
 * Convention: ℕ = {0, 1, 2, …} — non-negative integers (includes 0).
 * This matches the ISO 80000-2 convention and is the most common choice in
 * secondary-education set theory. The experiment UI documents this choice.
 */
export function isInN(v: ParsedNumber): boolean {
  return v.kind === 'integer' && v.value >= 0;
}

/** Returns true iff `v` is an integer (positive, zero, or negative). */
export function isInZ(v: ParsedNumber): boolean {
  return v.kind === 'integer';
}

/** Returns true iff `v` is rational (includes integers). */
export function isInQ(v: ParsedNumber): boolean {
  return v.kind === 'integer' || v.kind === 'rational';
}

/** Returns true iff `v` is real (integers, rationals, and irrationals). */
export function isInR(v: ParsedNumber): boolean {
  return v.kind === 'integer' || v.kind === 'rational' || v.kind === 'irrational';
}

// ── Classification ─────────────────────────────────────────────────────────────

/**
 * Returns the SMALLEST standard number set that contains `v`.
 *
 * Nesting: ℕ ⊂ ℤ ⊂ ℚ ⊂ ℝ ⊂ ℂ
 *
 * - Non-negative integer  → 'N'
 * - Negative integer      → 'Z'
 * - Non-integer rational  → 'Q'
 * - Irrational            → 'R'
 * - Complex (im ≠ 0)      → 'C'
 */
export function classifyNumber(v: ParsedNumber): NumberSet {
  switch (v.kind) {
    case 'integer':
      return v.value >= 0 ? 'N' : 'Z';
    case 'rational':
      return 'Q';
    case 'irrational':
      return 'R';
    case 'complex':
      return 'C';
  }
}

// ── Why-not explanations ───────────────────────────────────────────────────────

/**
 * Returns `null` if `v` belongs to `set`; otherwise returns an i18n key
 * explaining the first failing reason.
 *
 * Key scheme: `message.why.<SET>.<reason>`
 *
 * | Set | Reason key          | Meaning                              |
 * |-----|---------------------|--------------------------------------|
 * | N   | negative            | integer is negative                  |
 * | N   | not-integer         | value is not an integer at all       |
 * | Z   | not-integer         | value is not an integer              |
 * | Q   | irrational          | irrational numbers are not rational  |
 * | Q   | complex             | complex numbers are not rational     |
 * | R   | complex             | complex numbers are not real         |
 */
export function whyNotIn(set: NumberSet, v: ParsedNumber): string | null {
  // ℂ contains everything.
  if (set === 'C') return null;

  if (set === 'R') {
    if (v.kind === 'complex') return 'message.why.R.complex';
    return null;
  }

  if (set === 'Q') {
    if (v.kind === 'complex') return 'message.why.Q.complex';
    if (v.kind === 'irrational') return 'message.why.Q.irrational';
    return null; // integers and rationals are in ℚ
  }

  if (set === 'Z') {
    if (v.kind === 'rational' || v.kind === 'irrational' || v.kind === 'complex') {
      return 'message.why.Z.not-integer';
    }
    return null; // all integers are in ℤ
  }

  // set === 'N'
  if (v.kind !== 'integer') return 'message.why.N.not-integer';
  if (v.value < 0) return 'message.why.N.negative';
  return null;
}

// ── Free-text parser ──────────────────────────────────────────────────────────

/**
 * Parses a free-text string into a `ParsedNumber`, or returns `null` on
 * unparseable input.
 *
 * Recognised forms (all case-sensitive unless noted):
 * - Integer:    `'3'`, `'-5'`, `'0'`
 * - Fraction:   `'1/2'`, `'-3/4'` (via Fraction.parse; reduces to integer if
 *               den = 1 after reduction)
 * - Decimal:    `'0.5'`, `'-1.25'` (converted to Fraction via Fraction.from;
 *               returns integer if the fraction reduces to den = 1)
 * - Complex:    `'2+3i'`, `'1-2i'`, `'3i'`, `'-4i'`
 * - Irrational: `'√2'`, `'sqrt2'`, `'√3'`, `'π'`, `'pi'`, `'e'`
 */
export function parseNumber(text: string): ParsedNumber | null {
  const t = text.trim();
  if (t === '') return null;

  // ── Irrational tokens ──
  for (const entry of IRRATIONAL_TABLE) {
    if (entry.tokens.includes(t)) {
      return { kind: 'irrational', approx: entry.approx, label: entry.label };
    }
  }

  // ── Complex: pure imaginary e.g. '3i' or '-4i' ──
  const pureIm = t.match(/^(-?\d+(?:\.\d+)?)i$/);
  if (pureIm && pureIm[1] !== undefined) {
    const im = Number(pureIm[1]);
    if (Number.isFinite(im) && im !== 0) {
      return { kind: 'complex', re: 0, im };
    }
  }

  // ── Complex: a+bi or a-bi e.g. '2+3i', '1-2i' ──
  const complex = t.match(/^(-?\d+(?:\.\d+)?)\s*([+-]\s*\d+(?:\.\d+)?)i$/);
  if (complex && complex[1] !== undefined && complex[2] !== undefined) {
    const re = Number(complex[1]);
    const im = Number(complex[2].replace(/\s/g, ''));
    if (Number.isFinite(re) && Number.isFinite(im) && im !== 0) {
      return { kind: 'complex', re, im };
    }
  }

  // ── Fraction: contains '/' ──
  if (t.includes('/')) {
    try {
      const frac = Fraction.parse(t);
      // If it reduces to an integer, return integer kind
      if (frac.den === 1) {
        return { kind: 'integer', value: frac.num };
      }
      return { kind: 'rational', fraction: frac };
    } catch {
      return null;
    }
  }

  // ── Plain integer ──
  if (/^-?\d+$/.test(t)) {
    const n = parseInt(t, 10);
    if (Number.isFinite(n)) return { kind: 'integer', value: n };
    return null;
  }

  // ── Decimal ──
  if (/^-?\d+\.\d+$/.test(t)) {
    const n = Number(t);
    if (!Number.isFinite(n)) return null;
    try {
      const frac = Fraction.from(n);
      if (frac.den === 1) {
        return { kind: 'integer', value: frac.num };
      }
      return { kind: 'rational', fraction: frac };
    } catch {
      return null;
    }
  }

  return null;
}
